import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Renderer2,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { cloneDeep, isEqual } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { Store } from '@ngrx/store';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { GlobalConstants } from 'src/app/base/global.constants';
import { MonacoEditorHelperService } from 'src/app/services/monaco-editor-helper.service';
import { UtilService } from 'src/app/services/util.service';
import { Subject } from 'rxjs';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { TranslateService } from '@ngx-translate/core';

declare const monaco: any;

@Component({
  selector: 'vb-editor-with-autocomplete',
  templateUrl: './editor-with-autocomplete.component.html',
  styleUrls: ['./editor-with-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorWithAutocompleteComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false //NOSONAR
})
export class EditorWithAutocompleteComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private messageService = inject(MessageService);
  private appGlobalService = inject(AppGlobalService);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  public monacoHelper = inject(MonacoEditorHelperService);
  private utilService = inject(UtilService);
  private destroy$ = new Subject<void>();
  private baseService = inject(HttpBaseService);
  private translate = inject(TranslateService);

  @Input() element: any;
  @Input() disableField = false;
  private modelValue: any;
  @Input() formData: any = null;
  @Input() usedIn: any;
  @Output() notifySave: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() deletedValue: EventEmitter<any> = new EventEmitter<any>();

  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  @Output() public initialValueChange = new EventEmitter();
  allQueryStrings: any = [];
  value: any;
  backup: any;
  initialLoad = true;
  editorOptions = {
    language: GlobalConstants.queryStringLanguageIds.myCustomLanguage,
    wordWrap: 'on'
  };
  previewQueryEditorOptions = this.monacoHelper.getPreviewEditorOptions();
  showEditor = false;
  currenttablename: '';
  isQueryStringEditor = false; // Flag to track if editing a query string or datamodel query
  fromSave = false;
  jsonString: any;
  completionProviderId: any = null;
  appId: any;
  orgId: any;
  previewChecked = false;
  appMetaInfo: any;
  showPreviewDialog = false;
  formattedQuery = '';
  queryStringsMap: any = {};
  validationMessage = '';

  editorInstance: any;
  previewEditorInstance: any;
  private editorDisposable: any = null;
  originalValue: any;
  isFormDirty = false;

  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    this.orgId = this.appGlobalService.get('orgId');
    this.appMetaInfo = this.appGlobalService.get('appmetaInfo');
    this.queryStringsMap = this.appGlobalService.get('queryStrings');
  }

  replacePlaceholders(str: string, map: any) {
    if (str == null) {
      return null;
    }
    return str.replace(GlobalConstants.queryStringRegex.chipPatternNonEmpty, (fullMatch, key) => {
      // eslint-disable-next-line no-useless-escape
      return map && map[key] ? `\$\$\{${map[key]}\}` : fullMatch;
    });
  }

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.queryStringsMap = this.appGlobalService.get('queryStrings');
    const updatedQuery = this.replacePlaceholders(value, this.queryStringsMap);
    this.setValue(updatedQuery);
    this.modelValue = updatedQuery;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disableField = isDisabled;
  }

  toggleEditor(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      this.backup = this.value;
    }
    if (this.value) {
      this.backup = this.value;
    }
    this.showEditor = !this.showEditor;
    // Only set currenttablename from formData if it's not already set externally
    if (!this.currenttablename) {
      this.currenttablename = this.formData?.name;
    }
  }

  onShow(event) {
    this.queryStringsMap = this.appGlobalService.get('queryStrings');
    this.originalValue = this.value;
    this.isFormDirty = false;
    window.dispatchEvent(new Event('resize'));
    if (this.initialLoad) {
      this.setcustomLanguage();
      this.initialLoad = false;
    }
  }

  onResizeEnd(event) {
    window.dispatchEvent(new Event('resize'));
  }

  setValue(value) {
    this.value = value;
    this.jsonString = this.value;
    this.backup = this.value;
  }

  /**
   * Check if a value is empty (null, undefined, or empty string)
   */
  private isEmpty(value: any): boolean {
    return value == null || value === '';
  }

  /**
   * Check if two values are effectively equal (treats null, undefined, '' as same)
   */
  private areValuesEqual(val1: any, val2: any): boolean {
    // Both empty = equal
    if (this.isEmpty(val1) && this.isEmpty(val2)) {
      return true;
    }
    return isEqual(val1, val2);
  }

  closeDialog(event) {
    // Check if there are actual unsaved changes (not just null vs '' differences)
    const hasUnsavedChanges = !this.areValuesEqual(this.value, this.backup) && !this.fromSave;

    if (hasUnsavedChanges) {
      this.confirmationDialogService.confirm({
        message: 'You have unsaved changes. Are you sure you want to close this editor?',
        accept: () => {
          this.value = this.backup;
          this.showEditor = false;
          this.fromSave = false;
          this.currenttablename = ''; // Reset for next use
          this.isQueryStringEditor = false; // Reset for next use
          this.cdr.markForCheck();
        },
        reject: () => {
          this.showEditor = true;
          this.cdr.markForCheck();
        }
      });
    } else {
      this.showEditor = false;
      this.fromSave = false;
      this.currenttablename = ''; // Reset for next use
      this.isQueryStringEditor = false; // Reset for next use
      this.cdr.markForCheck();
    }
  }

  /**
   * Validates that all $ characters in the value are part of valid $${...} patterns
   * Returns true if valid, false if there are standalone $ characters
   */
  private hasStandaloneDollarSign(value: string): boolean {
    if (!value) return false;

    // Remove all valid $${...} patterns from the string
    const valueWithoutValidPatterns = value.replace(
      new RegExp(GlobalConstants.queryStringRegex.chipPatternNonEmpty.source, 'g'),
      ''
    );

    // Check if there are any remaining $ characters
    return valueWithoutValidPatterns.includes('$');
  }

  saveJson() {
    // Validate that value is not empty for QueryString
    if ((!this.value || !this.value.trim()) && this.formData?.isQueryString) {
      this.validationMessage = 'Query value is required';
      this.cdr.markForCheck();
      return;
    }

    // Validate no standalone $ characters in query value
    if (this.hasStandaloneDollarSign(this.value)) {
      this.validationMessage = '$ Character is not allowed in query. Use $${QueryStringName} format to reference other query strings';
      this.cdr.markForCheck();
      return;
    }

    this.validationMessage = '';
    this.fromSave = true;
    if (this.usedIn === 'referenceQueryEdit') {
      const success = this.saveEditQueryFromReference();
      if (!success) {
        this.fromSave = false;
        return; // Don't close dialog on error
      }
    } else {
      this.modelValue = this.value;
      this.jsonString = this.value;
      this.onChange(this.value);
    }
    this.toggleEditor();
  }

  ngOnDestroy(): void {
    // Signal destruction to all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up Monaco resources
    if (this.completionProviderId) {
      this.completionProviderId.dispose();
      this.completionProviderId = null;
    }
    if (this.editorDisposable) {
      this.editorDisposable.dispose();
      this.editorDisposable = null;
    }
    if (this.editorInstance) {
      this.editorInstance.dispose();
      this.editorInstance = null;
    }
    if (this.previewEditorInstance) {
      this.previewEditorInstance.dispose();
      this.previewEditorInstance = null;
    }
  }

  setcustomLanguage() {
    this.monacoHelper.registerCustomLanguage(GlobalConstants.queryStringLanguageIds.myCustomLanguage);
    this.setupCustomSuggestions();
  }

  onEditorInit(editor: any) {
    this.editorInstance = editor;
    // Dispose previous if exists
    if (this.editorDisposable) {
      this.editorDisposable.dispose();
    }
    this.editorDisposable = this.monacoHelper.setupHighlighting(editor, () =>
      Object.values(this.queryStringsMap as string[]).filter((name) => name)
    );

    // Track value changes to update isFormDirty
    editor.onDidChangeModelContent(() => {
      const currentValue = editor.getValue();
      this.isFormDirty = !this.areValuesEqual(currentValue, this.originalValue);
      this.cdr.markForCheck();
    });
  }

  onPreviewEditorInit(editor: any) {
    this.previewEditorInstance = editor;
  }

  // Update your existing setupCustomSuggestions to trigger highlighting after autocomplete
  setupCustomSuggestions() {
    // Dispose existing provider before registering a new one to prevent duplicate suggestions
    if (this.completionProviderId) {
      this.completionProviderId.dispose();
      this.completionProviderId = null;
    }
    this.completionProviderId = this.monacoHelper.getCompletionProvider(
      GlobalConstants.queryStringLanguageIds.myCustomLanguage,
      () => {
        // If editing a QueryString, exclude it from suggestions to prevent self-reference
        if (this.formData?.isQueryString && this.formData?.sid) {
          const clonedMap = cloneDeep(this.queryStringsMap);
          delete clonedMap[this.formData.sid];
          return clonedMap;
        }
        return this.queryStringsMap;
      },
      true // enable user variables for data model page
    );
  }

  async previewQuery(): Promise<void> {
    await this.getResolvedQuery();
    this.showPreviewDialog = true;
    this.cdr.markForCheck();
  }

  convertNamesToSids(content: string): string {
    if (!content) return content;
    // Build reverse map: name -> sid
    const nameToSidMap: Record<string, string> = {};

    // From queryStrings map (sid -> name)
    Object.entries(this.queryStringsMap).forEach(([sid, name]: [string, any]) => {
      if (name) nameToSidMap[name.toLowerCase()] = sid;
    });

    return content.replace(
      new RegExp(GlobalConstants.queryStringRegex.chipPatternNonEmpty.source, 'g'),
      (match, name) => {
        const sid = nameToSidMap[name.toLowerCase()];
        // eslint-disable-next-line no-useless-escape
        return sid ? `\$\${${sid}}` : match;
      }
    );
  }

  getResolvedQuery(): Promise<void> {
    return new Promise((resolve, reject) => {
      const convertedQuery = this.convertNamesToSids(this.value);

      // Build queryStringMap with only the SIDs that are used in the query
      const usedQueryStringMap: Record<string, string> = {};
      const regex = new RegExp(GlobalConstants.queryStringRegex.chipPatternNonEmpty.source, 'g');
      let match;

      while ((match = regex.exec(convertedQuery)) !== null) {
        const sid = match[1];
        // Get the name for this SID from queryStringsMap
        if (this.queryStringsMap[sid]) {
          usedQueryStringMap[sid] = this.queryStringsMap[sid];
        }
      }

      let payload: any = {};
      payload['appId'] = this.appId;
      payload['tableId'] = this.formData.sid;
      payload['query'] = convertedQuery;
      payload['queryStringMap'] = usedQueryStringMap;
      this.baseService['post'](ApiConstants.getQueryStringResolvedQuery, payload).subscribe({
        next: (query: any) => {
          this.formattedQuery = query[this.formData.sid];
          this.cdr.markForCheck();
          resolve();
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  closePreviewDialog() {
    this.showPreviewDialog = false;
  }

  saveEditQueryFromReference(): boolean {
    // Check if editing a QueryString or a DataModel (QueryView)
    if (this.formData?.isQueryString) {
      return this.saveQueryStringFromReference();
    } else {
      return this.saveQueryViewFromReference();
    }
  }

  private saveQueryStringFromReference(): boolean {
    const newValue = this.formatQuery(this.value);

    // Don't allow empty query string value
    if (!newValue || newValue.trim() === '') {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Query string value cannot be empty'
      });
      return false;
    }

    const formattedValue = this.formatQSNameToSid(newValue);

    // Track added/removed query string references
    const oldQuery = this.formData.value || '';
    const addedRemovedQS = this.utilService.changesMadeInQueryString(oldQuery, formattedValue);

    // Extract dependencies from the query string value
    const dependencies = this.extractDependenciesFromQuery(formattedValue);

    const queryStringData: any = {
      sid: this.formData.sid,
      name: this.formData.name,
      value: formattedValue,
      appId: this.appId,
      orgId: this.orgId
    };

    // Add dependencies if any exist
    if (Object.keys(dependencies).length > 0) {
      queryStringData.dependencies = dependencies;
    }

    this.baseService.put(ApiConstants.updateQueryString, queryStringData).subscribe({
      next: (response: any) => {
        if (response) {
          this.formData = {
            ...this.formData,
            value: formattedValue
          };
          // Emit deletedValue event to refresh querystring-list table when query strings are removed
          // This triggers deleteRemovedQueryStrings in querystring-list component
          if (addedRemovedQS['REMOVED']) {
            this.deletedValue.emit({ removed: Object.keys(addedRemovedQS['REMOVED']), refresh: true });
          } else {
            this.deletedValue.emit({ refresh: true });
          }
          // Emit notifySave event to refresh reference list
          this.notifySave.emit(true);
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        // Display error message to user
        let errorMessage = this.translate.instant('AN_ERROR_OCCURRED_WHILE_SAVING');
        if (err?.error?.MESSAGE) {
          errorMessage = err?.error?.MESSAGE;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000
        });

        this.notifySave.emit(false);
      }
    });
    return true;
  }

  /**
   * Save QueryView (DataModel) when editing from reference popup
   */
  private saveQueryViewFromReference(): boolean {
    const payload: any = {};

    const isMultiple = this.formData.t_query_type === 'multiple';
    const oldQuery = isMultiple ? this.formData.t_list_query : this.formData.t_detail_query;
    const newQuery = this.formatQSNameToSid(this.value);
    const addedRemovedQS = this.utilService.changesMadeInQueryString(oldQuery, newQuery);

    if (isMultiple) {
      payload['old_t_list_query'] = oldQuery;
      payload['t_list_query'] = newQuery;
    } else {
      payload['old_t_detail_query'] = oldQuery;
      payload['t_detail_query'] = newQuery;
    }

    payload['added'] = addedRemovedQS['ADDED'];
    payload['removed'] = addedRemovedQS['REMOVED'];
    payload['aid'] = this.appId;
    payload['orgId'] = this.orgId;
    payload['sid'] = this.formData.sid;
    payload['type'] = this.formData.type;
    payload['tabId'] = this.formData.tabId;

    // For QV parameter validation, include mandatory and optional filters
    if (this.formData.t_mandatory_filters) {
      payload['t_mandatory_filters'] = this.formData.t_mandatory_filters;
    }
    if (this.formData.t_optional_filters) {
      payload['t_optional_filters'] = this.formData.t_optional_filters;
    }
    payload['t_list_query'] = this.formData.t_list_query || "";
    payload['t_detail_query'] = this.formData.t_detail_query || "";


    this.baseService.put(ApiConstants.updateFields, [payload]).subscribe({
      next: (response: any) => {
        if (response) {
          this.formData = {
            ...this.formData,
            t_list_query: isMultiple ? newQuery : this.formData.t_list_query,
            t_detail_query: !isMultiple ? newQuery : this.formData.t_detail_query
          };
          // Emit deletedValue event to refresh querystring-list table when query strings are removed
          if (addedRemovedQS['REMOVED']) {
            this.deletedValue.emit({ removed: Object.keys(addedRemovedQS['REMOVED']), refresh: true });
          }
          // Emit notifySave event to refresh reference list
          this.notifySave.emit(true);
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        // Display error message to user
        let errorMessage = this.translate.instant('AN_ERROR_OCCURRED_WHILE_SAVING');
        if (err?.error?.MESSAGE) {
          errorMessage = err?.error?.MESSAGE;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000
        });

        this.notifySave.emit(false);
      }
    });
    return true;
  }

  formatQSNameToSid(query: string): string {
    return this.monacoHelper.formatQSNameToSid(query, this.queryStringsMap, this.appMetaInfo);
  }

  extractQueryStrings(query) {
    if (!query || typeof query !== 'string') {
      return [];
    }
    const regex = new RegExp(GlobalConstants.queryStringRegex.chipPatternNonEmpty.source, 'g');
    const matches = [...query.matchAll(regex)].map((match) => match[1]);

    return matches;
  }

  changesMadeInQueryString(oldQuery, newQuery) {
    const oldQueryQS = this.extractQueryStrings(oldQuery);
    const newQueryQS = this.extractQueryStrings(newQuery);

    const addedSet = newQueryQS.filter((item) => !oldQueryQS.find((oldItem) => oldItem === item));
    const removedSet = oldQueryQS.filter((item) => !newQueryQS.find((newItem) => newItem === item));

    let changes = {};
    if (addedSet.length > 0) {
      changes['ADDED'] = addedSet;
    }
    if (removedSet.length > 0) {
      changes['REMOVED'] = removedSet;
    }
    return changes;
  }

  formatQuery(query: string): string {
    if (!query) return query;

    return query
      .replace(GlobalConstants.queryStringRegex.newlinePattern, ' ')
      .replace(GlobalConstants.queryStringRegex.tabPattern, ' ')
      .replace(GlobalConstants.queryStringRegex.multiSpacePattern, ' ')
      .trim();
  }

  /**
   * Extract dependencies from query string content
   * Returns a map of { sid: name } for all referenced query strings
   */
  extractDependenciesFromQuery(content: string): Record<string, string> {
    if (!content) return {};

    const dependencies: Record<string, string> = {};
    // Build sid -> name map
    const sidToNameMap: Record<string, string> = {};
    Object.entries(this.queryStringsMap).forEach(([sid, name]: [string, any]) => {
      if (name) sidToNameMap[sid] = name;
    });
    Object.entries(this.appMetaInfo).forEach(([sid, name]: [string, any]) => {
      if (name && !sidToNameMap[sid]) {
        sidToNameMap[sid] = name;
      }
    });
    // Extract all $${sid} references from content
    const regex = new RegExp(GlobalConstants.queryStringRegex.chipPatternNonEmpty.source, 'g');
    let match;

    while ((match = regex.exec(content)) !== null) {
      const sid = match[1];
      if (sidToNameMap[sid]) {
        dependencies[sid] = sidToNameMap[sid];
      }
    }

    return dependencies;
  }
}



